// Liquiditaetsplanung – Einstiegsseite.
//
// Laedt direkt einen aktiven Plan fuer den ersten verfuegbaren Mandanten ×
// aktuelles Jahr (legt einen an, falls keiner existiert) und navigiert
// transparent zum Editor.
//
// Es gibt bewusst keinen separaten „Plan-Liste / Plan anlegen"-Schritt:
// pro Mandant × Jahr existiert genau ein aktiver Plan, der bei Erstaufruf
// automatisch aus der Historie befuellt wird.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import { ErrorState, LoadingState, PageHeader } from '@/components/ui'
import type { PlanningCompany } from './types.ts'

export function PlanListPage({ basePath = '/finanzen/liquiditaet/planung', title = 'Liquiditätsplanung' }: { basePath?: string; title?: string } = {}) {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [diagnostic, setDiagnostic] = useState<string[]>([])

  useEffect(() => {
    void bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function pushDiag(msg: string) {
    setDiagnostic((prev) => [...prev, `${new Date().toLocaleTimeString('de-DE')} ${msg}`])
  }

  async function bootstrap() {
    setError(null)
    setDiagnostic([])
    try {
      // Schritt 0: Route ueberhaupt erreichbar? __ping hat keine Auth/Module-Guards.
      pushDiag('Prüfe Backend-Route /api/liquidity-planning/__ping …')
      try {
        const ping = await apiClient.get<{ ok: boolean; buildSha: string; serverTime: string }>(
          '/liquidity-planning/__ping',
        )
        pushDiag(`Backend OK – buildSha=${ping.data.buildSha}, serverTime=${ping.data.serverTime}`)
      } catch (pingErr) {
        const status = (pingErr as { response?: { status?: number } })?.response?.status
        pushDiag(`Backend-Route NICHT gemountet (HTTP ${status ?? '?'}). Plesk: Build neu starten + Passenger restarten (touch tmp/restart.txt).`)
        throw pingErr
      }

      pushDiag('GET /api/liquidity-planning/companies …')
      const compRes = await apiClient.get<PlanningCompany[]>('/liquidity-planning/companies')
      const companies = Array.isArray(compRes.data) ? compRes.data.filter((c) => Boolean(c.key)) : []
      pushDiag(`${companies.length} Mandanten geladen: [${companies.map((c) => c.key).join(', ')}]`)
      if (companies.length === 0) {
        setError('Keine Mandanten verfügbar. Bitte unter Einstellungen → Firma → Adapter → SAP eine SAP-Verbindung anlegen (companyDb pflichtig).')
        return
      }

      const firstKey = companies[0]!.key
      const year = new Date().getFullYear()
      pushDiag(`GET /api/liquidity-planning/plans/active?company=${firstKey}&year=${year} …`)
      const planRes = await apiClient.get<{ planId: number }>(
        `/liquidity-planning/plans/active?company=${encodeURIComponent(firstKey)}&year=${year}`,
      )
      pushDiag(`Plan-ID: ${planRes.data.planId} → navigate`)
      navigate(`${basePath}/${planRes.data.planId}`, { replace: true })
    } catch (err) {
      const status = (err as { response?: { status?: number; data?: unknown } })?.response?.status
      const data = (err as { response?: { data?: unknown } })?.response?.data
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler'
      pushDiag(`FEHLER ${status ?? ''}: ${message}${data ? ` · body=${JSON.stringify(data).slice(0, 200)}` : ''}`)
      setError(message)
    }
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={title}
        breadcrumbs={[{ label: 'Finanzen' }, { label: title }]}
      />
      {error ? (
        <ErrorState
          title="Plan konnte nicht geladen werden"
          description={error}
          onRetry={bootstrap}
          details={diagnostic.length > 0 ? diagnostic.join('\n') : undefined}
        />
      ) : (
        <LoadingState label="Plan wird geladen / angelegt …" />
      )}
    </div>
  )
}
